import React, { useState, useEffect } from 'react';
import { Modal, Badge, Table, Spinner, Row, Col } from 'react-bootstrap';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import type { Event } from '../api/generated/model';
import { getSlingShifts, getEventsId, postEventsIdShifts, deleteEventsIdShifts } from '../api/generated/arrayscapeGamingDashboardAPI';
import SlingLocationMappingsModal from './SlingLocationMappingsModal';

interface HostDetailModalProps {
  show: boolean;
  onHide: () => void;
  event: Event | null;
  onEventUpdated?: (updatedEvent: Event) => void;
  // When false, the modal is view-only: shift assign/remove and the location
  // mapping editors are disabled (the shift endpoints are admin/events_manager
  // only). Defaults to false so callers must explicitly grant edit rights.
  canEdit?: boolean;
}

const formatTime = (dateStr?: string, timezone?: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(timezone ? { timeZone: timezone } : {}),
    });
  } catch {
    return '';
  }
};

// Calendar date of an instant, in the supplied timezone (e.g. "Jul 21, 2026").
// Matches the format used by the "Open ... in Sling" header button.
const formatDate = (dateStr?: string, timezone?: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(timezone ? { timeZone: timezone } : {}),
    });
  } catch {
    return '';
  }
};

// Get short timezone abbreviation (e.g. "EDT", "CST"), with Arizona special-cased to "AZ-MST"
const getShortTimezone = (dateStr?: string, timezone?: string): string => {
  if (!dateStr || !timezone) return '';
  try {
    // Special case Arizona - matches the backend logic in datetime.ZoneString()
    if (timezone === 'US/Arizona' || timezone === 'America/Phoenix') {
      return 'AZ-MST';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: timezone,
    }).formatToParts(d);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || '';
  } catch {
    return '';
  }
};

const statusColor = (status?: string): string => {
  switch (status) {
    case 'published': return 'success';
    case 'draft': return 'warning';
    default: return 'secondary';
  }
};

const HostDetailModal: React.FC<HostDetailModalProps> = ({ show, onHide, event: eventProp, onEventUpdated, canEdit = false }) => {
  const [showMappingsModal, setShowMappingsModal] = useState(false);
  const [locationShifts, setLocationShifts] = useState<any[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [shiftsError, setShiftsError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmReassign, setConfirmReassign] = useState<any>(null);
  const [refreshedEvent, setRefreshedEvent] = useState<Event | null>(null);
  const queryClient = useQueryClient();

  // Use refreshed event data if available, otherwise fall back to prop
  const event = refreshedEvent || eventProp;

  // Refetch event data when refreshKey changes (e.g., after mapping change or shift assignment)
  useEffect(() => {
    if (refreshKey === 0 || !eventProp?.id) return;

    const refetch = async () => {
      try {
        const updated = await getEventsId(eventProp.id!);
        setRefreshedEvent(updated as Event);
        if (onEventUpdated) {
          onEventUpdated(updated as Event);
        }
      } catch {
        // ignore - will use stale data
      }
    };

    refetch();
  }, [refreshKey, eventProp?.id]);

  // Reset refreshed event when the modal opens with a new event
  useEffect(() => {
    setRefreshedEvent(null);
  }, [eventProp?.id]);

  const programLocation = event?.program?.location || '';
  const locationMapping = event?.customer?.locations?.find(
    (m: any) => (m.location || '') === programLocation
  );
  const hasSlingMapping = !!locationMapping?.sling_location_id;
  const slingLocationId = locationMapping?.sling_location_id;
  const slingLocationSrcId = locationMapping?.sling_location_src_id;
  const eventStartTime = event?.program?.time?.start?.time;

  // Mutation for adding a shift to this event
  const addShift = useMutation({
    mutationFn: async ({ shiftId, shiftSrcId }: { shiftId: string; shiftSrcId: string }) => {
      return postEventsIdShifts(event!.id!, {
        shift_id: shiftId,
        shift_src_id: shiftSrcId,
      });
    },
    onSuccess: (result: any) => {
      if (onEventUpdated && result?.event) {
        onEventUpdated(result.event);
      }
      if (result?.warning) {
        alert(result.warning);
      }
      setRefreshKey(k => k + 1);
      setConfirmReassign(null);
    },
  });

  // Mutation for removing a shift from this event
  const removeShift = useMutation({
    mutationFn: async ({ shiftId, shiftSrcId }: { shiftId: string; shiftSrcId: string }) => {
      return deleteEventsIdShifts(event!.id!, {
        shift_id: shiftId,
        shift_src_id: shiftSrcId,
      });
    },
    onSuccess: (updatedEvent: any) => {
      if (onEventUpdated && updatedEvent) {
        onEventUpdated(updatedEvent);
      }
      setRefreshKey(k => k + 1);
    },
  });

  // Fetch shifts for this location on this date
  useEffect(() => {
    if (!show || !hasSlingMapping || !eventStartTime || !slingLocationId) {
      setLocationShifts([]);
      return;
    }

    const fetchShifts = async () => {
      setLoadingShifts(true);
      setShiftsError(null);
      try {
        // Day boundaries are computed server-side in the SlingLocation's
        // own timezone (resolved from location_id), so the request just
        // sends the calendar date in that same timezone as a plain
        // YYYY-MM-DD string. We deliberately format in the LOCATION's tz
        // (not the event's) — the question is "what shifts at this
        // location were on this calendar day," and the location's
        // wall-clock day is the right reference. Sending an ISO datetime
        // here would 400 — the spec types these as `format: date`.
        const eventDate = new Date(eventStartTime);
        const locationTz = locationMapping?.sling_location?.timezone;
        const localDate = eventDate.toLocaleDateString('en-CA', { timeZone: locationTz || undefined });

        let allShifts: any[] = [];
        let cursor: string | undefined;

        do {
          const params: any = cursor
            ? { cursor, limit: 50 }
            : {
                location_id: slingLocationId,
                src_id: slingLocationSrcId,
                // Same day on both sides — half-open [start, end+1day)
                // is applied server-side in the location's tz.
                start_date: localDate,
                end_date: localDate,
                sort_field: 'start_time',
                sort_direction: 'asc',
                limit: 50,
              };

          const resp = await getSlingShifts(params);
          allShifts = allShifts.concat((resp as any)?.data || []);
          cursor = (resp as any)?.pagination?.cursor;
        } while (cursor);

        setLocationShifts(allShifts);
      } catch (err: any) {
        // Don't silently render an empty list — surface the failure so
        // the operator knows the panel didn't load. Falls through to
        // err.message for non-axios errors.
        setLocationShifts([]);
        setShiftsError(err?.response?.data?.message || err?.message || 'Failed to load shifts');
      }
      setLoadingShifts(false);
    };

    fetchShifts();
  }, [show, hasSlingMapping, eventStartTime, slingLocationId, slingLocationSrcId, refreshKey]);

  if (!event) return null;

  const shifts = event.shifts || [];
  const hasShifts = shifts.length > 0;
  const timezone = event.program?.time?.start?.timezone;

  // Day view URL for the header button
  const eventDateStr = event.program?.time?.start?.time
    ? new Date(event.program.time.start.time).toISOString().split('T')[0]
    : '';
  const slingDayUrl = eventDateStr
    ? `https://app.getsling.com/shifts?mode=day&date=${eventDateStr}`
    : '';

  // Check if a shift is already assigned to this event
  const isShiftAssigned = (shiftId: string) => {
    return shifts.some(s => s.id === shiftId);
  };

  const handleShiftClick = (s: any) => {
    if (!canEdit) return;
    if (!event.id || !slingLocationSrcId || !s.id) return;

    if (isShiftAssigned(s.id)) return; // already assigned

    if (s.has_event && s.event_ids?.length > 0) {
      // Shift is assigned to another event — confirm
      setConfirmReassign(s);
    } else {
      // Unmapped shift — assign directly
      addShift.mutate({ shiftId: s.id, shiftSrcId: slingLocationSrcId });
    }
  };

  return (
    <>
      <Modal show={show && !showMappingsModal} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-3">
            <span>Shift Details</span>
            {slingDayUrl && (
              <a
                href={slingDayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Open {event.program?.time?.start?.time ? new Date(event.program.time.start.time).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  ...(timezone ? { timeZone: timezone } : {}),
                }) : ''} in Sling
              </a>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'hidden', padding: 0 }}>
          <Row className="g-0" style={{ height: '100%' }}>
            {/* Left: Event & Sling details */}
            <Col md={6} style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '1rem' }}>
              {/* Event Details */}
              <h6 className="text-muted mb-2">Event</h6>
              <Table borderless size="sm" className="mb-2">
                <tbody>
                  {event.customer?.name && (
                    <tr>
                      <td className="fw-semibold" style={{ width: '130px' }}>Customer</td>
                      <td>
                        {event.customer.name}
                        {event.customer.primary_source?.address?.state && (
                          <span className="text-muted">, {event.customer.primary_source.address.state}</span>
                        )}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="fw-semibold">Sling Location</td>
                    <td>
                      {hasSlingMapping ? (
                        <span>
                          {locationMapping.sling_location?.name || 'Unknown'}
                          {locationMapping.sling_location?.state && (
                            <span className="text-muted"> ({locationMapping.sling_location.state})</span>
                          )}
                          {event.customer?.id && canEdit && (
                            <button className="btn btn-sm btn-outline-secondary ms-2 py-0" onClick={() => setShowMappingsModal(true)}>
                              <i className="bi bi-pencil me-1"></i>Edit
                            </button>
                          )}
                        </span>
                      ) : (
                        <span>
                          <span className="text-danger">Not mapped</span>
                          {event.customer?.id && canEdit && (
                            <button className="btn btn-sm btn-warning ms-2 py-0" onClick={() => setShowMappingsModal(true)}>
                              <i className="bi bi-geo-alt me-1"></i>Create Mapping
                            </button>
                          )}
                          {hasShifts && (() => {
                            const shiftLocs = Array.from(new Set(shifts.map(s => s.location?.name).filter(Boolean)));
                            if (shiftLocs.length === 0) return null;
                            return (
                              <div className="small text-warning mt-1" style={{ whiteSpace: 'normal' }}>
                                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                A host is assigned but this program location has no mapping. The assigned shift is at Sling location <strong>{shiftLocs.join(', ')}</strong> — map this location to it if it's the same place, otherwise fix the program location or reassign the host.
                              </div>
                            );
                          })()}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-semibold">Program</td>
                    <td>{event.program?.program || '\u2014'}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold">Program Location</td>
                    <td>{programLocation || (() => {
                      const addr = event.customer?.primary_source?.address;
                      if (!addr) return <span className="text-muted">Not set</span>;
                      const parts = [addr.line1, addr.line2, addr.city, addr.state, addr.zip].filter(Boolean);
                      return parts.length > 0 ? parts.join(', ') : <span className="text-muted">Not set</span>;
                    })()}</td>
                  </tr>
                  {event.program?.time?.start?.time && (
                    <tr>
                      <td className="fw-semibold">Event Time</td>
                      <td>
                        {new Date(event.program.time.start.time).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          ...(timezone ? { timeZone: timezone } : {}),
                        })}{' '}
                        {formatTime(event.program.time.start.time, timezone)}
                        {event.program.time.end?.time && ` - ${formatTime(event.program.time.end.time, timezone)}`}
                        {' '}{getShortTimezone(event.program.time.start.time, timezone)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Assigned Shifts */}
              {hasShifts && (
                <>
                  <hr className="my-2" />
                  <h6 className="text-muted mb-2">Assigned Shifts</h6>
                  {shifts.map((shift) => (
                    <div key={shift.id} className="border rounded p-2 mb-2 d-flex align-items-start justify-content-between">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Badge bg={statusColor(shift.status)}>{shift.status || 'Unknown'}</Badge>
                          {shift.user && (
                            <span className="fw-semibold">{shift.user.first_name} {shift.user.last_name}</span>
                          )}
                        </div>
                        {shift.location?.name && (
                          <div className="small text-muted">
                            <i className="bi bi-geo-alt me-1"></i>{shift.location.name}
                          </div>
                        )}
                        {shift.start_time && (() => {
                          const shiftTz = shift.location?.timezone || timezone;
                          // The shift's own date is shown, not just its time. A shift left
                          // behind by a rescheduled program keeps the *old* date, and with
                          // only a time on this card there was nothing to give that away.
                          //
                          // event_time_mismatch is computed server-side: the shift's period
                          // does not overlap the event's, so it cannot be covering it. That
                          // catches a leftover from a reschedule and a shift attached to the
                          // wrong program on the right day alike.
                          const mismatch = !!shift.event_time_mismatch;
                          return (
                            <div className={`small ${mismatch ? 'text-danger fw-semibold' : 'text-muted'}`}>
                              {formatDate(shift.start_time, shiftTz)}{' · '}
                              {formatTime(shift.start_time, shiftTz)} - {formatTime(shift.end_time, shiftTz)}{' '}
                              {getShortTimezone(shift.start_time, shiftTz)}
                              {mismatch && (
                                <span
                                  className="d-block"
                                  title="This shift does not overlap the event's scheduled time, so it cannot be covering it. It is most likely left over from before the program was rescheduled, or assigned to the wrong program."
                                >
                                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                  Does not overlap this event
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {shift.summary && (
                          <div className="small text-muted" style={{ whiteSpace: 'pre-line' }}>{shift.summary}</div>
                        )}
                      </div>
                      {canEdit && (
                        <button
                          className="btn btn-sm btn-outline-danger py-0 ms-2"
                          disabled={removeShift.isPending}
                          onClick={() => {
                            if (shift.id && slingLocationSrcId) {
                              removeShift.mutate({ shiftId: shift.id, shiftSrcId: slingLocationSrcId });
                            }
                          }}
                          title="Remove shift"
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </Col>

            {/* Right: Shifts at this location */}
            <Col md={6} style={{ borderLeft: '1px solid #dee2e6', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '1rem' }}>
              <h6 className="mb-2">
                Shifts at Location
                {locationMapping?.sling_location?.name && (
                  <span className="text-muted fw-normal"> — {locationMapping.sling_location.name}</span>
                )}
              </h6>
              {/* Tz-mismatch warning — the date we send is the calendar
                  day in the LOCATION's tz, which is the right reference
                  for shifts. When the event itself is recorded in a
                  different tz that resolves to a different UTC offset
                  at the event's instant, the operator could otherwise
                  reach this view expecting "shifts on the event's date"
                  and get the off-by-one between event-tz day and
                  location-tz day. Compare by offset (not string) so
                  aliases like US/Eastern vs America/New_York don't
                  false-positive — same approach as events.go:170. */}
              {(() => {
                const evTz = event.program?.time?.start?.timezone;
                const locTz = locationMapping?.sling_location?.timezone;
                if (!hasSlingMapping || !evTz || !locTz || !eventStartTime) return null;
                // Compute UTC offset of `eventStartTime` interpreted in
                // each tz. Different offsets → real mismatch. Wrapped
                // in try/catch because Intl throws on invalid tz names
                // (e.g. "Local") and we'd rather skip the warning than
                // crash the modal.
                const offsetMin = (instant: Date, tz: string): number | null => {
                  try {
                    const utcStr = instant.toLocaleString('en-US', { timeZone: 'UTC' });
                    const tzStr = instant.toLocaleString('en-US', { timeZone: tz });
                    return (new Date(tzStr).getTime() - new Date(utcStr).getTime()) / 60000;
                  } catch {
                    return null;
                  }
                };
                const d = new Date(eventStartTime);
                const evOff = offsetMin(d, evTz);
                const locOff = offsetMin(d, locTz);
                if (evOff === null || locOff === null || evOff === locOff) return null;
                return (
                  <div className="alert alert-warning small mb-2 py-2">
                    <i className="bi bi-exclamation-triangle me-1" />
                    Event runs in <strong>{evTz}</strong>, but the Sling location is in{' '}
                    <strong>{locTz}</strong>. Shifts below are for the location's
                    calendar day, which may differ from the event's local date.
                  </div>
                );
              })()}
              {!hasSlingMapping ? (
                <div className="text-muted small">No sling location mapped</div>
              ) : loadingShifts ? (
                <div className="text-center py-3"><Spinner size="sm" /></div>
              ) : shiftsError ? (
                <div className="alert alert-danger small mb-0 py-2">
                  <i className="bi bi-exclamation-triangle-fill me-1" />
                  {shiftsError}
                </div>
              ) : locationShifts.length === 0 ? (
                <div className="text-muted small">No shifts found for this date</div>
              ) : (
                <>
                  {canEdit && (
                    <div className="text-muted small mb-2">Click a shift to assign it to this event</div>
                  )}
                  <Table size="sm" striped hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Host</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {locationShifts.map((s: any) => {
                        const isAssigned = isShiftAssigned(s.id);
                        const isOtherEvent = s.has_event && !isAssigned;
                        return (
                          <tr
                            key={s.id}
                            className={isOtherEvent ? 'text-muted' : ''}
                            style={isAssigned ? { backgroundColor: 'rgba(13, 110, 253, 0.1)' } : {}}
                          >
                            <td>
                              {(() => {
                                const shiftTz = s.location?.timezone || timezone;
                                return <>{formatTime(s.start_time, shiftTz)} - {formatTime(s.end_time, shiftTz)} {getShortTimezone(s.start_time, shiftTz)}</>;
                              })()}
                            </td>
                            <td>
                              {s.user ? `${s.user.first_name || ''} ${s.user.last_name || ''}`.trim() : '\u2014'}
                            </td>
                            <td className="text-end">
                              {isAssigned ? (
                                <Badge bg="primary">Assigned</Badge>
                              ) : isOtherEvent ? (
                                <button className="btn btn-sm btn-outline-warning py-0" disabled={!canEdit} onClick={() => handleShiftClick(s)}>
                                  Assign
                                </button>
                              ) : (
                                <button className="btn btn-sm btn-outline-primary py-0" disabled={!canEdit} onClick={() => handleShiftClick(s)}>
                                  Assign
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  {addShift.isPending && (
                    <div className="text-center py-2"><Spinner size="sm" /> Assigning...</div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <button className="btn btn-sm btn-secondary" onClick={onHide}>Close</button>
        </Modal.Footer>
      </Modal>

      {/* Confirm assign when shift is already on another event */}
      <Modal show={!!confirmReassign} onHide={() => setConfirmReassign(null)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Confirm Shift Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">This shift is already assigned to another event. Do you want to also assign it to this event?</p>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-column gap-2">
          <button
            className="btn btn-primary w-100"
            disabled={addShift.isPending}
            onClick={() => {
              if (confirmReassign && slingLocationSrcId) {
                addShift.mutate({ shiftId: confirmReassign.id, shiftSrcId: slingLocationSrcId });
              }
            }}
          >
            {addShift.isPending ? 'Assigning...' : 'Assign Shift'}
          </button>
          <button className="btn btn-outline-secondary w-100" onClick={() => setConfirmReassign(null)}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {event.customer?.id && (
        <SlingLocationMappingsModal
          show={showMappingsModal}
          onHide={() => {
            setShowMappingsModal(false);
            setRefreshKey(k => k + 1);
            queryClient.invalidateQueries({ queryKey: ['events-new'] });
          }}
          customerId={event.customer.id}
          customerName={event.customer.name || ''}
        />
      )}
    </>
  );
};

export default HostDetailModal;
